import React from "react";
import PropTypes from "prop-types";
import useEmblaCarousel from "embla-carousel-react";
import AutoHeight from "embla-carousel-auto-height";
// Material imports
import withStyles from "@mui/styles/withStyles";
// Limitless imports
import TestimonialScreen from "./TestimonialScreen";
import CSATScreen from "./CSATScreen";
import ThankYouScreen from "./ThankYouScreen";
import ResolutionScreen from "./ResolutionScreen";
import ExpertFeedbackScreen from "./ExpertFeedbackScreen";
import RateFinishSlide from "./RateFinishSlide";
import RateFinish010Feedback from "./RateFinish010Feedback";
import { MESSAGE_STATE_RERATE_YOUR_EXPERIENCE, FEEDBACK, EXPERT_FEEDBACK, FURTHER_CONTACT } from "../../../utils";
import FurtherContactScreen from "./FurtherContactScreen";
const calculateStartStep = (message, skipTestimonial) => {
  var _message$custClientRe, _message$custClientRe2, _message$custClientRe3, _message$custClientRe4, _message$custClientRe5, _message$custClientRe6, _message$custClientRe7, _message$custClientRe8;
  let step = 0;
  if (message.footerState === MESSAGE_STATE_RERATE_YOUR_EXPERIENCE) {
    step = 0;
  } else if (message.custResolved && message.customerScore && !message.custTestimonial && !skipTestimonial) {
    var _message$custClientSc;
    if (((_message$custClientSc = message.custClientScoreScheme) === null || _message$custClientSc === void 0 ? void 0 : _message$custClientSc.name) === FURTHER_CONTACT) {
      step = 2;
    } else {
      step = 1;
    }
  } else if ((message.custTestimonial || skipTestimonial) && message.isNPSEnabled) {
    var _message$custClientSc2;
    switch ((_message$custClientSc2 = message.custClientScoreScheme) === null || _message$custClientSc2 === void 0 ? void 0 : _message$custClientSc2.name) {
      case FEEDBACK:
        if (typeof ((_message$custClientRe = message.custClientReview) === null || _message$custClientRe === void 0 ? void 0 : _message$custClientRe.score) !== "number") {
          return step = 2;
        } else {
          return step = 4;
        }
      case EXPERT_FEEDBACK:
        if (typeof ((_message$custClientRe2 = message.custClientReview) === null || _message$custClientRe2 === void 0 ? void 0 : _message$custClientRe2.resolved) !== "boolean") {
          return step = 2;
        } else if (typeof ((_message$custClientRe3 = message.custClientReview) === null || _message$custClientRe3 === void 0 ? void 0 : _message$custClientRe3.expertScore) !== "number" || !((_message$custClientRe4 = message.custClientReview) !== null && _message$custClientRe4 !== void 0 && _message$custClientRe4.expertFeedback)) {
          return step = 3;
        } else if (!((_message$custClientRe5 = message.custClientReview) !== null && _message$custClientRe5 !== void 0 && _message$custClientRe5.generalFeedback)) {
          return step = 4;
        } else {
          return step = 5;
        }
      case FURTHER_CONTACT:
        if (!message.custTestimonial && (((_message$custClientRe6 = message.custClientReview) === null || _message$custClientRe6 === void 0 ? void 0 : _message$custClientRe6.resolved) !== "boolean" || ((_message$custClientRe7 = message.custClientReview) === null || _message$custClientRe7 === void 0 ? void 0 : _message$custClientRe7.resolved) !== null) && !((_message$custClientRe8 = message.custClientReview) !== null && _message$custClientRe8 !== void 0 && _message$custClientRe8.generalFeedback)) {
          return step = 2;
        } else {
          return step = 3;
        }
      default:
        return "";
    }
  }
  return step;
};
const resolvedTrue = "Thank you. Your feedback helps us get better. If there is anything else you'd like to share about your experience, you can let us know below.";
const resolvedFalse = "Sorry we weren't able to resolve your issue. We will use all of your feedback to help us get better. If there is anything else you'd like to share about your experience, you can let us know below.";
const RateFinishMessageFooter = ({
  classes,
  onCancelDialog,
  message,
  onSubmitCSATScore,
  onSubmitNPS,
  onSubmitNPSFeedback,
  onSubmitExpertScore,
  onSubmitExpertFeedback,
  onSubmitTestimonial,
  onSubmitResoluation,
  onSubmitResoluationContact,
  onSubmitGeneralFeedback,
  isNPSRequired = false,
  isMobileDevice
}) => {
  var _message$custClientSc3, _message$custClientSc4, _message$custClientSc5, _message$custClientSc6, _message$custClientRe9, _message$custClientRe10, _message$custClientRe11, _message$custClientRe12;
  const [skipTestimonial, setSkipTestimonial] = React.useState(false);
  const [slideIndex, setSlideIndex] = React.useState(calculateStartStep(message, skipTestimonial));
  const autoheight = React.useRef(AutoHeight());
  const isRtl = document.documentElement.dir === "rtl";
  const carouselOptions = React.useMemo(() => ({
    draggable: false,
    startIndex: slideIndex,
    speed: 15,
    dragFree: false,
    direction: isRtl ? "rtl" : "ltr"
  }), [slideIndex, isRtl]);
  const [emblaRef, emblaApi] = useEmblaCarousel(carouselOptions, [autoheight.current]);
  const setStep = React.useCallback((step, clearHeight = false) => {
    if (emblaApi) {
      if (clearHeight) {
        emblaApi.containerNode().style.height = "100%";
      } else {
        setSlideIndex(step || slideIndex + 1);
      }
    }
  }, [emblaApi, slideIndex]);
  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.scrollTo(slideIndex);
    }
  }, [emblaApi, slideIndex]);
  const step = ((_message$custClientSc3 = message.custClientScoreScheme) === null || _message$custClientSc3 === void 0 ? void 0 : _message$custClientSc3.name) === FURTHER_CONTACT ? 2 : 1;
  const updateStepHandle = score => onSubmitCSATScore(score, () => setStep(step));
  const handleSubmitNPS = score => onSubmitNPS(score, () => setStep());
  const handleSubmitResoluation = resolved => onSubmitResoluation(resolved, () => setStep());
  const handleSubmitResoluationContact = custClientResolved => onSubmitResoluationContact(custClientResolved, () => setStep());
  const handleExpertScore = expertscore => onSubmitExpertScore(expertscore, () => setStep(4, true));
  const handleExpertFeedback = expertFeedback => onSubmitExpertFeedback(expertFeedback, () => setStep());
  const handleSubmitNPSFeedback = feedback => onSubmitNPSFeedback(feedback, () => setStep());
  const handleSubmitGeneralFeedback = generalFeedback => onSubmitGeneralFeedback(generalFeedback, () => setStep());
  const handleSubmitTestimonial = testimonial => {
    if (testimonial) {
      onSubmitTestimonial(testimonial);
    }
    if (isNPSRequired) {
      message.custTestimonial = testimonial;
      setStep(calculateStartStep(message, true));
    } else if (onCancelDialog) {
      onCancelDialog();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "embla",
    ref: emblaRef
  }, /*#__PURE__*/React.createElement("div", {
    className: "embla__container"
  }, /*#__PURE__*/React.createElement(RateFinishSlide, {
    key: 0
  }, /*#__PURE__*/React.createElement(CSATScreen, {
    message: message,
    containerClass: classes.container,
    onSubmitCSATScore: updateStepHandle,
    isMobileDevice: isMobileDevice
  })), /*#__PURE__*/React.createElement(RateFinishSlide, {
    height: 280,
    key: 1
  }, /*#__PURE__*/React.createElement(TestimonialScreen, {
    testimonial: message.custTestimonial,
    onSubmitTestimonial: handleSubmitTestimonial,
    containerClass: classes.container,
    getButtonLabel: text => text !== "" ? "Send" : isNPSRequired ? "Next" : "Cancel",
    onCancelDialog: isNPSRequired ? () => setSkipTestimonial(true) : onCancelDialog,
    id: "testimonialTextarea",
    custScoreScheme: message.custScoreScheme,
    customerScore: message.customerScore
  })), ((_message$custClientSc4 = message.custClientScoreScheme) === null || _message$custClientSc4 === void 0 ? void 0 : _message$custClientSc4.name) === FURTHER_CONTACT && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RateFinishSlide, {
    key: 2
  }, /*#__PURE__*/React.createElement(FurtherContactScreen, {
    message: message,
    onSubmitResoluationContact: handleSubmitResoluationContact,
    onSubmitTestimonial: handleSubmitTestimonial,
    onCancelDialog: onCancelDialog
  })), /*#__PURE__*/React.createElement(RateFinishSlide, {
    height: 140,
    key: 3
  }, /*#__PURE__*/React.createElement(ThankYouScreen, {
    onCancelDialog: () => onCancelDialog(true)
  }))), ((_message$custClientSc5 = message.custClientScoreScheme) === null || _message$custClientSc5 === void 0 ? void 0 : _message$custClientSc5.name) === FEEDBACK && /*#__PURE__*/React.createElement(RateFinish010Feedback, {
    classes: classes,
    onCancelDialog: onCancelDialog,
    message: message,
    handleSubmitNPS: handleSubmitNPS,
    handleSubmitNPSFeedback: handleSubmitNPSFeedback
  }), ((_message$custClientSc6 = message.custClientScoreScheme) === null || _message$custClientSc6 === void 0 ? void 0 : _message$custClientSc6.name) === EXPERT_FEEDBACK && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RateFinishSlide, {
    height: "auto",
    key: 2
  }, (message.footerState === MESSAGE_STATE_RERATE_YOUR_EXPERIENCE || typeof ((_message$custClientRe9 = message.custClientReview) === null || _message$custClientRe9 === void 0 ? void 0 : _message$custClientRe9.resolved) !== "boolean") && /*#__PURE__*/React.createElement(ResolutionScreen, {
    message: message,
    onSubmitResoluation: handleSubmitResoluation
  })), /*#__PURE__*/React.createElement(RateFinishSlide, {
    key: 3
  }, /*#__PURE__*/React.createElement(ExpertFeedbackScreen, {
    message: message,
    containerClass: classes.container,
    onSubmitExpertScore: handleExpertScore,
    onSubmitExpertFeedback: handleExpertFeedback
  })), /*#__PURE__*/React.createElement(RateFinishSlide, {
    height: "auto",
    key: 4
  }, /*#__PURE__*/React.createElement(TestimonialScreen, {
    headingTitle: (_message$custClientRe10 = message.custClientReview) !== null && _message$custClientRe10 !== void 0 && _message$custClientRe10.resolved ? resolvedTrue : resolvedFalse,
    testimonial: (_message$custClientRe11 = (_message$custClientRe12 = message.custClientReview) === null || _message$custClientRe12 === void 0 ? void 0 : _message$custClientRe12.generalFeedback) !== null && _message$custClientRe11 !== void 0 ? _message$custClientRe11 : "",
    onSubmitTestimonial: handleSubmitGeneralFeedback,
    containerClass: classes.container,
    onCancelDialog: onCancelDialog,
    getButtonLabel: () => "Submit",
    id: "testimonialTextareaWithGeneralFeedbackHeading"
  })), /*#__PURE__*/React.createElement(RateFinishSlide, {
    height: 140,
    key: 5
  }, /*#__PURE__*/React.createElement(ThankYouScreen, {
    onCancelDialog: () => onCancelDialog(true)
  })))));
};
const styles = () => {
  return {
    swipeableViewContainer: {
      maxHeight: "100%",
      width: 300,
      justifyContent: "center",
      alignItems: "center",
      position: "relative"
    },
    container: {
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 24
    }
  };
};
export default withStyles(styles)(RateFinishMessageFooter);
RateFinishMessageFooter.propTypes = {
  classes: PropTypes.object.isRequired,
  onCancelDialog: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  onSubmitTestimonial: PropTypes.func.isRequired,
  onSubmitCSATScore: PropTypes.func.isRequired,
  onSubmitNPS: PropTypes.func.isRequired,
  onSubmitNPSFeedback: PropTypes.func.isRequired,
  isNPSRequired: PropTypes.bool,
  onSubmitExpertScore: PropTypes.func,
  onSubmitResoluationContact: PropTypes.func,
  onSubmitExpertFeedback: PropTypes.func,
  onSubmitResoluation: PropTypes.func,
  onSubmitGeneralFeedback: PropTypes.func,
  isMobileDevice: PropTypes.bool.isRequired
};